import { Navigate, useParams } from 'react-router-dom';

/**
 * Path aliases kept alive after the catering rebuild renamed several route
 * groups.
 *
 * Pages carried over from the platform still link to the old paths in dozens
 * of places — `/customers/42`, `/admin/users`, `/sales/invoices`. Rewriting
 * every one of those call sites would touch forty files for no user-visible
 * gain and a real chance of missing one. Redirecting instead means the old
 * links keep working, the address bar normalises to the current path, and
 * there is exactly one place to look when a route moves again.
 *
 * `replace` is set so the alias never lands in browser history — a user
 * pressing Back after following one should go where they came from, not
 * bounce through the redirect.
 */

/** Redirect that carries a single `:id` through to the new path. */
function WithId({ to }: { to: (id: string) => string }) {
  const { id } = useParams();
  return <Navigate to={to(id ?? '')} replace />;
}

/**
 * Old path → new path. Anything containing `:id` gets the param-preserving
 * component; the rest are plain redirects.
 */
const ALIASES: Array<[string, string]> = [
  // Admin and organisation moved under /setup.
  ['/admin/users', '/setup/users'],
  ['/admin/users/invite', '/setup/users/invite'],
  ['/admin/sessions', '/setup/sessions'],
  ['/admin/login-history', '/setup/login-history'],
  ['/admin/numbering', '/setup/numbering'],
  ['/admin/global-settings', '/setup/global-settings'],
  ['/admin/imports', '/setup/import'],
  ['/admin/audit-log', '/setup/audit-log'],
  ['/organization/companies', '/setup/companies'],
  ['/organization/branches', '/setup/branches'],
  ['/organization/departments', '/setup/departments'],

  // Party and warehouse masters moved under /masters.
  ['/setup/batch-numbering', '/setup/numbering'],
  ['/customers', '/masters/customers'],
  ['/masterdata/customers', '/masters/customers'],
  ['/suppliers', '/masters/suppliers'],
  ['/warehouses', '/masters/warehouses'],

  // Sales became Billing.
  ['/sales', '/billing/dashboard'],
  ['/sales-invoices', '/billing/invoices'],
  ['/sales/invoices', '/billing/invoices'],
  ['/sales/invoices/new', '/billing/invoices/new'],
  ['/sales/customer-statement', '/billing/statements'],
  ['/sales/credit-notes', '/billing/credit-notes'],

  // Purchase sub-paths renamed for plain language.
  ['/purchase-bills', '/purchase/bills'],
  ['/purchase/grn', '/purchase/receipts'],
  ['/purchase/supplier-quotes', '/purchase/quotes'],
  ['/purchase/supplier-quotes/new', '/purchase/quotes/new'],
  ['/purchase/evaluation', '/purchase/supplier-evaluation'],

  // Stores.
  ['/inventory', '/inventory/dashboard'],
  ['/inventory/balances', '/inventory/stock'],
  ['/inventory/stock-adjustments', '/inventory/adjustments'],

  // Section landings that used to be bare paths.
  ['/hr', '/hr/dashboard'],
  ['/attendance', '/attendance/dashboard'],
  ['/leave', '/leave/dashboard'],
  ['/payroll', '/payroll/dashboard'],
  ['/accounting', '/accounting/dashboard'],
  ['/expense', '/expenses/dashboard'],
  ['/expense/claims', '/expenses/claims'],
  ['/expense/claims/new', '/expenses/claims/new'],
  ['/expense/categories', '/expenses/categories'],
  ['/expense/advances', '/expenses/advances'],
  ['/quality', '/quality/dashboard'],
  ['/maintenance', '/maintenance/dashboard'],
  ['/compliance', '/compliance/dashboard'],
  ['/contract', '/contracts'],
  ['/contract/new', '/contracts'],

  // Renamed pages.
  ['/accounting/chart-of-accounts', '/accounting/coa'],
  ['/accounting/reports/customer-aging', '/reports/customer-aging'],
  ['/accounting/reports/profit-loss', '/reports/pnl'],
  ['/quality/non-conformances', '/quality/ncr'],
  ['/reports/health', '/reports/business-health'],
];

/** Aliases that must carry an `:id` through to the destination. */
const ID_ALIASES: Array<[string, (id: string) => string]> = [
  ['/customers/:id', (id) => `/masters/customers/${id}`],
  ['/suppliers/:id', (id) => `/masters/suppliers/${id}`],
  ['/warehouses/:id', (id) => `/masters/warehouses/${id}`],
  ['/admin/users/:id', () => '/setup/users'],
  ['/organization/departments/:id', (id) => `/setup/departments/${id}`],
  ['/contract/:id', (id) => `/contracts/${id}`],
  ['/compliance/:id', (id) => `/compliance/documents/${id}`],
  ['/sales/invoices/:id', (id) => `/billing/invoices/${id}`],
  ['/expense/claims/:id', (id) => `/expenses/claims/${id}`],
  ['/expense/claims/:id/edit', (id) => `/expenses/claims/${id}`],
  ['/purchase/grn/:id', (id) => `/purchase/receipts/${id}`],
  ['/purchase/supplier-quotes/:id', (id) => `/purchase/quotes/${id}`],
];

export const legacyRedirectRoutes = [
  ...ALIASES.map(([from, to]) => ({
    path: from,
    element: <Navigate to={to} replace />,
  })),
  ...ID_ALIASES.map(([from, to]) => ({
    path: from,
    element: <WithId to={to} />,
  })),
];
